import { FC, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon } from 'src/components';
import { IconName } from 'src/components/Icon';
import * as S from './styles';

export const BLOB_ONBOARDING_STEP_COUNT = 4;

const msgs = defineMessages({
  progress: { id: 'blob_onboarding_progress', defaultMessage: '{current} of {total}' },
  progressLabel: {
    id: 'onboarding_progress_indicator_label',
    defaultMessage: 'Onboarding progress',
  },
  back: { id: 'web_header_back' },
  loading: { id: 'blob_onboarding_loading', defaultMessage: 'Loading' },
});

type StepPageProps = {
  /** Leave out to hide the step progress */
  currentStep?: number;
  onBack?: () => void;
  children: ReactNode;
};

/** Layout skeleton while a step's data loads — matches the onboarding frame. */
export const StepLoader: FC<{ currentStep?: number }> = ({ currentStep }) => {
  const intl = useIntl();

  return (
    <StepPage currentStep={currentStep}>
      <S.Content
        data-testid="blob-onboarding-skeleton"
        aria-busy="true"
        aria-live="polite"
        aria-label={intl.formatMessage(msgs.loading)}
      >
        <S.Header>
          <S.SkeletonFlag aria-hidden />
          <S.SkeletonTitle aria-hidden />
          <S.SkeletonSubtitle aria-hidden />
        </S.Header>
        <S.SkeletonOptions aria-hidden>
          <S.SkeletonOption />
          <S.SkeletonOption />
          <S.SkeletonOption />
        </S.SkeletonOptions>
      </S.Content>
    </StepPage>
  );
};

/** The blob page frame: background, back button and, in onboarding, step progress. */
export const StepPage: FC<StepPageProps> = ({ currentStep, onBack, children }) => {
  const intl = useIntl();

  return (
    <S.Root>
      <S.TopBar>
        {onBack && (
          <S.BackButton
            type="button"
            onClick={onBack}
            aria-label={intl.formatMessage(msgs.back)}
            data-testid="blob-onboarding-back"
          >
            <Icon name="chevronLeft" />
          </S.BackButton>
        )}
        {currentStep !== undefined && (
          <S.Progress>
            <S.Segments
              role="progressbar"
              aria-label={intl.formatMessage(msgs.progressLabel)}
              aria-valuemin={1}
              aria-valuemax={BLOB_ONBOARDING_STEP_COUNT}
              aria-valuenow={currentStep}
              data-testid="blob-onboarding-progress"
            >
              {Array.from({ length: BLOB_ONBOARDING_STEP_COUNT }, (_, index) => (
                <S.Segment key={index} $filled={index < currentStep} />
              ))}
            </S.Segments>
            <S.Count>
              {intl.formatMessage(msgs.progress, {
                current: currentStep,
                total: BLOB_ONBOARDING_STEP_COUNT,
              })}
            </S.Count>
          </S.Progress>
        )}
      </S.TopBar>
      {children}
    </S.Root>
  );
};

type StepLayoutProps = {
  currentStep: number;
  title: string;
  subtitle?: string;
  flagUrl?: string;
  language?: string;
  /** Shown in place of the flag */
  icon?: IconName;
  iconIncludesBadge?: boolean;
  onBack?: () => void;
  children: ReactNode;
};

/** A centred onboarding step with a flag or icon, title and subtitle above its content. */
export const StepLayout: FC<StepLayoutProps> = ({
  currentStep,
  title,
  subtitle,
  flagUrl,
  language = '',
  icon,
  iconIncludesBadge = false,
  onBack,
  children,
}) => (
  <StepPage currentStep={currentStep} onBack={onBack}>
    <S.Content>
      <S.Header>
        {icon ? (
          <S.IconBadge data-testid="step-icon" $fullSize={iconIncludesBadge}>
            <Icon name={icon} />
          </S.IconBadge>
        ) : (
          flagUrl && <S.Flag src={flagUrl} alt={language} data-testid="flag" />
        )}
        <S.Title>{title}</S.Title>
        {subtitle && <S.Subtitle>{subtitle}</S.Subtitle>}
      </S.Header>
      {children}
    </S.Content>
  </StepPage>
);
